import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FileSignature, CheckCircle, Download, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { getToken } from '../../services/tokenService';

const API = process.env.REACT_APP_BACKEND_URL;
const ROLES = { cooper: "COOPER'S", admin: 'Administration', super_admin: 'Administration' };

/** Statut « Signé » + bouton de signature en ligne (membre) d'une Proposition ESS. */
export function PropositionSignatureAction({ devisId, docId, expired }) {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!devisId) return;
    axios.get(`${API}/api/batico/proposition/${encodeURIComponent(devisId)}/signature-espace`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    }).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, [devisId]);

  if (!info || info.status === 'absent') return null;
  if (info.status === 'signed') {
    return (
      <div className="flex flex-col gap-1 flex-1 lg:flex-none" data-testid={`proposition-signee-${docId}`}>
        <span className="inline-flex items-center justify-center gap-1 rounded-md bg-emerald-100 px-2 py-1 text-xs font-bold text-emerald-800">
          <CheckCircle className="h-3.5 w-3.5" />
          Signé le {new Date(info.signed_at_iso).toLocaleDateString('fr-FR')}
        </span>
        {info.signe_pour_membre && (
          <span className="text-[10px] leading-tight text-center text-slate-600" data-testid={`proposition-signe-pour-membre-${docId}`}>
            Signé pour le membre par <strong>{info.signe_pour_membre.par_nom}</strong> ({ROLES[info.signe_pour_membre.par_role] || info.signe_pour_membre.par_role})
          </span>
        )}
        {info.download_path && (
          <a href={`${API}${info.download_path}`} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1 text-[11px] font-semibold text-emerald-700 hover:underline"
            data-testid={`proposition-signee-pdf-${docId}`}>
            <Download className="h-3 w-3" /> Devis signé
          </a>
        )}
      </div>
    );
  }
  if (expired) return null;
  if (info.whatsapp_url) {
    return (
      <div className="flex flex-col gap-1 flex-1 lg:flex-none">
        {info.sign_path && (
          <Button size="sm" variant="outline" onClick={() => navigate(info.sign_path)}
            className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid={`proposition-signer-pour-membre-${docId}`}>
            <FileSignature className="h-4 w-4 mr-1" /> Signer pour le membre
          </Button>
        )}
      <a href={info.whatsapp_url} target="_blank" rel="noopener noreferrer"
        title={info.whatsapp_numero_connu ? 'Ouvre WhatsApp avec le message et le lien de signature' : 'Numéro du membre inconnu : choisissez le contact dans WhatsApp'}
        className="inline-flex items-center justify-center gap-1 rounded-md bg-[#25D366] px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 flex-1 lg:flex-none"
        data-testid={`proposition-whatsapp-${docId}`}>
        <MessageCircle className="h-4 w-4" /> Signature via WhatsApp
      </a>
      </div>
    );
  }
  if (!info.sign_path) return null;
  return (
    <Button size="sm" onClick={() => navigate(info.sign_path)}
      className="bg-[#0a3d62] hover:bg-[#0c4a75] text-white font-semibold flex-1 lg:flex-none"
      data-testid={`proposition-signer-${docId}`}>
      <FileSignature className="h-4 w-4 mr-1" /> Signer en ligne
    </Button>
  );
}
